/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Select } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import type { VenueRef } from '@sportninja/common/features/game/gameListView/types'

import { font, weightSemiBold } from 'src/components/css'

import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

export const VENUE_FIELD_ID = 'venue-editor-location'
export const SURFACE_FIELD_ID = 'venue-editor-surface'

export interface VenueSelection {
  venueId: string
  facilityId: string | null
}

export interface VenueCellEditorParams {
  venuesById: Map<string, VenueRef>
}

/**
 * Location and surface are picked TOGETHER, in one editor, or not at all.
 *
 * A surface belongs to one building, so a location change must replace or clear
 * the surface in the same request — a lone Location dropdown would leave the game
 * on a surface belonging to the building it just left. Surface is optional: with
 * none picked, the move sends `facility_id: null` and clears it.
 *
 * The value in and out is the row's `venueId`; the chosen surface rides along
 * on `data` for the grid's change handler to read, because AG Grid commits one
 * column at a time.
 */
const VenueCellEditor = ({
  data,
  onValueChange,
  stopEditing,
  venuesById,
}: CustomCellEditorProps<GameGridRow> & VenueCellEditorParams) => {
  const [venueId, setVenueId] = useState<string | null>(data?.venueId ?? null)
  const [facilityId, setFacilityId] = useState<string | null>(
    data?.facilityId ?? null
  )

  const venue = venueId === null ? undefined : venuesById.get(venueId)
  //  Inactive surfaces are excluded: the update endpoint refuses a move onto
  //  one, so offering it would only produce a rejection the admin cannot act
  //  on. The surface the game ALREADY sits on stays listed even if inactive,
  //  so the field can show what is currently set.
  const facilities = (venue?.facilities ?? []).filter(
    (facility) =>
      facility.is_active !== false || facility.id === data?.facilityId
  )

  const isSameVenue = venueId === data?.venueId

  //  Surface is optional: with none picked, the move clears the old one.
  const canApply =
    venueId !== null && (!isSameVenue || facilityId !== data?.facilityId)

  /*
   * Both dropdowns portal into THIS panel, so a pick lands inside the editor and
   * `stopEditingWhenCellsLoseFocus` does not treat it as a click-outside.
   *
   * Anchoring to the panel's own node rather than a queried `.ag-popup-editor`
   * matters twice over: AG Grid recreates that element when the editor
   * re-renders, which would take the portal with it, and the grid root — the
   * previous anchor — sits OUTSIDE the popup, so choosing a location tore the
   * whole editor down before a surface could be picked.
   *
   * Held as STATE, not a ref, and the fields wait for it: a ref is still null on
   * first paint, and the location list opens on first paint (`defaultOpen`), so
   * it would portal to `document.body` — outside the grid — and the very first
   * pick would close the editor. The extra render costs nothing.
   */
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const insideEditor = () => panel ?? document.body

  const commit = () => {
    if (!canApply || venueId === null) {
      stopEditing(true)
      return
    }
    //  Encoded as one string so a single column commit carries both halves.
    onValueChange(`${venueId}|${facilityId ?? ''}`)
    stopEditing()
  }

  return (
    /*
     * No blanket `preventDefault` on mousedown here: antd opens a Select ON
     * mousedown, so suppressing it left the surface dropdown permanently shut
     * (only the `defaultOpen` venue field worked). Focus containment is handled
     * instead by `getPopupContainer` below.
     */
    <div css={panelStyles} ref={setPanel}>
      {panel !== null && (
        <>
          <label css={fieldStyles}>
            <span css={labelStyles}>{gameViewsLabel('gameGridColVenue')}</span>
            <Select
              //  Explicit ids: without them both fields advertise the SAME
              //  `aria-controls`, so nothing — assistive tech included — can
              //  tell which option list belongs to which field.
              id={VENUE_FIELD_ID}
              //  Focused, but NOT opened on mount. The panel stacks Location
              //  above Surface, so a list that opens by itself lands directly on
              //  top of the Surface field and hides the value underneath it.
              //  Typing or ArrowDown opens it, which is the normal Select
              //  behaviour anyway.
              autoFocus
              value={venueId}
              onChange={(next: string) => {
                setVenueId(next)
                //  A surface belongs to exactly one venue, so changing the venue
                //  invalidates whatever was picked.
                setFacilityId(null)
              }}
              options={Array.from(venuesById.values()).map((option) => ({
                value: option.id,
                label: option.name_full ?? option.name ?? option.id,
              }))}
              showSearch
              optionFilterProp='label'
              style={selectStyle}
              getPopupContainer={insideEditor}
            />
          </label>

          <label css={fieldStyles}>
            <span css={labelStyles}>
              {gameViewsLabel('gameGridColFacility')}
            </span>
            <Select
              id={SURFACE_FIELD_ID}
              value={facilityId}
              onChange={(next: string) => setFacilityId(next)}
              options={facilities.map((facility) => ({
                value: facility.id,
                label: facility.name ?? facility.id,
              }))}
              placeholder={gameViewsLabel('bulkChooseFacility')}
              disabled={venueId === null || facilities.length === 0}
              style={selectStyle}
              getPopupContainer={insideEditor}
            />
          </label>
        </>
      )}

      <div css={actionsStyles}>
        <button
          type='button'
          css={secondaryButtonStyles}
          onClick={() => stopEditing(true)}
        >
          {gameViewsLabel('bulkCancel')}
        </button>
        <button
          type='button'
          css={primaryButtonStyles}
          disabled={!canApply}
          onClick={commit}
          data-testid='venue-editor-apply'
        >
          {gameViewsLabel('bulkApply')}
        </button>
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

//  antd sizes its Select through `style`, not a class, so this stays a plain
//  object — hoisted out of the JSX so both fields share the one width.
const selectStyle = { width: 240 }

const panelStyles = css`
  ${font.body}
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
`

const fieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const labelStyles = css`
  ${weightSemiBold}
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const actionsStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const secondaryButtonStyles = css`
  ${font.body}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
`

const primaryButtonStyles = css`
  ${font.body}
  ${weightSemiBold}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: none;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export default VenueCellEditor
